// src/features/streams/components/StreamOutputsEditor.tsx

import { Stack, Group, Select, NumberInput, ActionIcon, Button, Text } from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import {
  STREAM_RESOLUTION_PRESETS,
  STREAM_OUTPUT_CONSTRAINTS,
} from '@/lib/constants/stream';
import { StreamUtils } from '../StreamUtils';

export interface StreamOutputItem {
  /** Index into STREAM_RESOLUTION_PRESETS */
  resolutionIndex: number;
  /** Total bitrate (video + audio) in kbps */
  totalKbps: number;
}

interface StreamOutputsEditorProps {
  outputs: StreamOutputItem[];
  onChange: (outputs: StreamOutputItem[]) => void;
}

/** All resolution options (used for the first row which has no upper-bound constraint) */
const allResolutionOptions = STREAM_RESOLUTION_PRESETS.map((r, i) => ({
  value: String(i),
  label: r.label,
}));

/**
 * Build allowed resolution options for a given row.
 * Each row can only select the same resolution or lower than the row above.
 * STREAM_RESOLUTION_PRESETS is ordered highest-to-lowest, so "lower or equal"
 * means resolutionIndex >= the previous row's resolutionIndex.
 */
function getResolutionOptions(prevResolutionIndex: number | null) {
  if (prevResolutionIndex === null) return allResolutionOptions;
  return allResolutionOptions.filter((_, i) => i >= prevResolutionIndex);
}

export function createDefaultOutput(): StreamOutputItem {
  return StreamUtils.defaultOutputForPosition(0);
}

export function StreamOutputsEditor({ outputs, onChange }: StreamOutputsEditorProps) {
  const canAdd = outputs.length < STREAM_OUTPUT_CONSTRAINTS.MAX_OUTPUTS;
  const canRemove = outputs.length > STREAM_OUTPUT_CONSTRAINTS.MIN_OUTPUTS;

  const updateOutput = (index: number, patch: Partial<StreamOutputItem>) => {
    const next = outputs.map((o, i) => (i === index ? { ...o, ...patch } : o));
    onChange(StreamUtils.clampOutputsDownstream(next));
  };

  const addOutput = () => {
    const lastOutput = outputs[outputs.length - 1];
    onChange([
      ...outputs,
      StreamUtils.defaultOutputForPosition(outputs.length, lastOutput.resolutionIndex),
    ]);
  };

  const removeOutput = (index: number) => {
    const next = outputs.filter((_, i) => i !== index);
    onChange(StreamUtils.clampOutputsDownstream(next));
  };

  const handleResolutionChange = (index: number, val: string | null) => {
    if (!val) return;
    const resIndex = Number(val);
    const preset = STREAM_RESOLUTION_PRESETS[resIndex];
    updateOutput(index, {
      resolutionIndex: resIndex,
      totalKbps: preset.recommendedKbps,
    });
  };

  return (
    <Stack gap="xs">
      <Text size="sm" fw={500}>Stream Outputs</Text>
      {outputs.length > 1 && (
        <Text size="xs" c="dimmed">
          Multiple outputs require an encoder that supports keyframe-aligned multi-bitrate output.
          Minimum upload bandwidth should be 2x the total of all output bitrates.
        </Text>
      )}

      {outputs.map((output, i) => {
        const prevResIndex = i > 0 ? outputs[i - 1].resolutionIndex : null;
        const options = getResolutionOptions(prevResIndex);

        return (
          <Group key={i} gap="xs" wrap="nowrap" align="flex-end">
            <Select
              label={i === 0 ? 'Resolution' : undefined}
              data={options}
              value={String(output.resolutionIndex)}
              onChange={(val) => handleResolutionChange(i, val)}
              allowDeselect={false}
              style={{ flex: 1 }}
            />
            <NumberInput
              label={i === 0 ? 'Bitrate (kbps)' : undefined}
              value={output.totalKbps}
              onChange={(val) => {
                if (typeof val === 'number') updateOutput(i, { totalKbps: val });
              }}
              min={STREAM_OUTPUT_CONSTRAINTS.MIN_BITRATE_KBPS}
              max={STREAM_OUTPUT_CONSTRAINTS.MAX_BITRATE_KBPS}
              w={130}
            />
            {canRemove ? (
              <ActionIcon
                variant="subtle"
                color="red"
                size="lg"
                onClick={() => removeOutput(i)}
                aria-label="Remove output"
              >
                <IconTrash size={16} />
              </ActionIcon>
            ) : (
              // Spacer to keep alignment when remove button is hidden
              <ActionIcon variant="transparent" size="lg" style={{ visibility: 'hidden' }}>
                <IconTrash size={16} />
              </ActionIcon>
            )}
          </Group>
        );
      })}

      {canAdd && (
        <Button
          variant="light"
          size="xs"
          leftSection={<IconPlus size={14} />}
          onClick={addOutput}
          w="fit-content"
        >
          Add Output
        </Button>
      )}
    </Stack>
  );
}
